import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from './../../../environments/environment';

import { timer } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { max } from 'moment';
import { Transaction } from '../../models/transaction';
import { CustomerService } from '../../core/services/customer.service';


@Injectable({
  providedIn: 'root',
})
export class RequestService {
  private endpointPrefix = environment.apiUrl;

  constructor(private http: HttpClient, private customerService: CustomerService) { }

  public getAllRequest() {
    return this.http.get(this.endpointPrefix + '/reconciliations');
  }
  public getRequestResult(id: number) {
    return this.http.get(
      this.endpointPrefix + '/reconciliations/reports/' + id
    );
  }
  public getRequest(id: number) {
    return this.http.get(this.endpointPrefix + '/reconciliations/' + id);
  }

  public getRequestStatusById(id: number) {
    return this.http.get(this.endpointPrefix + '/reconciliations/' + id + '/status');
  }

  public getArchivedRequest(customerId: number, page: number, size: number, filters?: any) {

    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (filters) {
      if (filters.name) {
        params = params.set('name', filters.name);
      }

      if (filters.dateStart) {
        params = params.set('dateStart', filters.dateStart);
      }

      if (filters.dateEnd) {
        params = params.set('dateEnd', filters.dateEnd);
      }

      if (filters.source1) {
        if (Array.isArray(filters.source1)) {
          filters.source1.forEach((code: string) => {
            params = params.append('source1', code);
          });
        } else {
          params = params.set('source1', filters.source1);
        }
      }

      if (filters.source2) {
        params = params.set('source2', filters.source2);
      }

      if (filters.status) {
        if (Array.isArray(filters.status)) {
          filters.status.forEach((st: string) => {
            params = params.append('status', st);
          });
        } else {
          params = params.set('status', filters.status);
        }
      }

      if (filters.reconciliationBatchId) {
        if (Array.isArray(filters.reconciliationBatchId)) {
          filters.reconciliationBatchId.forEach((id: number) => {
            params = params.append('reconciliationBatchId', id.toString());
          });
        } else {
          params = params.set('reconciliationBatchId', filters.reconciliationBatchId);
        }
      }

      if (filters.operationTypeId) {
        if (Array.isArray(filters.operationTypeId)) {
          filters.operationTypeId.forEach((id: number) => {
            params = params.append('operationTypeId', id.toString());
          });
        } else {
          params = params.set('operationTypeId', filters.operationTypeId);
        }
      }

      if (filters.archive !== undefined && filters.archive !== null && filters.archive !== '') {
        params = params.set('archive', filters.archive);
      }
    }

    if (!this.customerService.getIsJubooAgent()) {

      return this.http.get(
        `${this.endpointPrefix}/reconciliations`,
        { params }
      );

    } else {

      const id = customerId || 0;

      return this.http.get(
        `${this.endpointPrefix}/reconciliations/customer/${id}/test-sources`,
        { params }
      );
    }
  }

  public createRequest(payload: any): any {
    return this.http.post(this.endpointPrefix + '/reconciliations', payload);
  }
  public pollUntilConditionIsTrue(id: number): any {
    return timer(0, 5000).pipe(
      switchMap(() =>
        this.http.get(this.endpointPrefix + '/reconciliations/reports/' + id)
      ),
      takeWhile(
        (res: any) =>
          res.reconciliation.status === 'DONE' ||
          res.reconciliation.status === 'RECONCILIATION_ERROR'
      )
    );
  }
  public archiveRequest(id: number) {
    return this.http.get(
      this.endpointPrefix + '/reconciliations/' + id + '/archive'
    );
  }

  download(e: any) {
    return this.http.get(
      this.endpointPrefix +
      `/reconciliations/download/${e.reconciliation.id}/multiple?type=${e.source.code}`,
      { responseType: 'blob' }
    )
  }


  public calculateSummary(result: any) {
    let totalAmount1 = 0;
    let totalAmount2 = 0;
    let percentage1 = 0;
    let percentage2 = 0;
    let totalDifference = 0;
    let percentageDifference = 0;
    let totalMissing = 0;
    let numberMissingSource1 = 0;
    let totalAmountMissingSource1 = 0;
    let totalAmountEcartSource1 = 0;
    let totalAmountEcartSource2 = 0;
    let totalAmountMissingSource2 = 0;
    let numberMissingSource2 = 0;
    let totalMisMatch = 0;
    let totalCorrect = 0;
    let totalEcartSource1 = 0;
    let totalEcartSource2 = 0;

    result.results.forEach((result: any) => {
      if (result.status === 'MISSING') totalMissing++;
      else if (result.status === 'MISMATCH') totalMisMatch++;
      else if (result.status === 'CORRECT') totalCorrect++;

      if (result.amount1 !== null) totalAmount1 += result.amount1;

      if (result.amount2 !== null) totalAmount2 += result.amount2;
      if (result.amount1 == null && result.amount2 !== null) {
        numberMissingSource2++;
        totalAmountMissingSource2 += result.amount2;
      }

      if (result.amount2 == null && result.amount1 !== null) {
        numberMissingSource1++;
        totalAmountMissingSource1 += result.amount1;
      }

      if (result.amount1 && result.amount2 && result.amount1 < result.amount2) {
        totalAmountEcartSource1 += Math.abs(result.amount1 - result.amount2);
      }
      if (result.amount1 && result.amount2 && result.amount1 > result.amount2) {
        totalAmountEcartSource2 += Math.abs(result.amount1 - result.amount2);
      }
    });
    const total = Math.max(totalAmount1, totalAmount2);
    percentage1 = Math.round((totalAmount1 / total) * 100);
    percentage2 = Math.round((totalAmount2 / total) * 100);
    totalDifference = Math.abs(totalAmount1 - totalAmount2);
    percentageDifference = Math.round((totalDifference / total) * 100);

    return {
      totalAmount1: totalAmount1,
      totalAmount2: totalAmount2,
      percentage1: percentage1,
      percentage2: percentage2,
      totalDifference: totalDifference,
      percentageDifference: percentageDifference,
      totalMissing: totalMissing,
      totalMisMatch: totalMisMatch,
      totalCorrect: totalCorrect,
      numberMissingSource1: numberMissingSource1,
      numberMissingSource2: numberMissingSource2,
      totalAmountMissingSource1: totalAmountMissingSource1,
      totalAmountMissingSource2: totalAmountMissingSource2,
      totalAmountEcartSource1: totalAmountEcartSource1,
      totalAmountEcartSource2: totalAmountEcartSource2,
      totalEcartSource1: totalAmountMissingSource1 + totalAmountEcartSource1,
      totalEcartSource2: totalAmountMissingSource2 + totalAmountEcartSource2,
    };
  }

  performMatch(tx_ids: number[]) {
    return this.http.post<Transaction[]>(`${this.endpointPrefix}/transactions/manual-matching`, tx_ids)
  }

  performBreakMatch(payload: any) {
    return this.http.post<Transaction[]>(`${this.endpointPrefix}/transactions/manual-unmatching`, payload)
  }

  reStartReconciliation(id: number) {
    return this.http.put(`${this.endpointPrefix}/reconciliations/${id}/replay-without-change`, {})
  }

  manualMatchTransactions(sourceIds: number[], tx_ids: number[] | undefined): any {
    const body = { sourceIds: sourceIds, transactionIds: tx_ids };
    return this.http.post(`${this.endpointPrefix}/transactions/manual-match-transactions`, body)
  }
}
